import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Retweet,
  Reblog,
  Tooltip,
  TwitterBlueVerified
  // @ts-ignore
} from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import { AutomationId } from 'utils/automationUtil'
import WarningInfo from './WarningInfo'
import {
  StyledContainer,
  StyledContentContainer,
  StyledHeaderContainer,
  StyledHeaderLeft,
  StyledHeaderRight,
  StyledIconContainer,
  StyledContent,
  StyledInfoContainer,
  StyledActionContainer,
  StyledMetadataContainer,
  StyledOtherAuthorsSpan,
  StyledUserLink
} from './index.css'

const TWITTER_URL = `https://twitter.com/`

type SoundbiteDefaultFrameProps = {
  replySection?: {
    display: boolean
    author: undefined | Array<string>
  }
  retweetSection?: {
    display: boolean
    author: undefined | string
    url: undefined | string
    isVerified: undefined | boolean
  }
  reblogSection?: {
    display: boolean
    author: undefined | string
    url: undefined | string
  }
  hasWarningInfo?: boolean
  dateItem: React.ReactNode
  domainIcon: React.ReactElement
  headerInfo: React.ReactElement
  postContent: React.ReactNode
  actionItems: React.ReactNode
  metadataItems: React.ReactNode
  mediaItems: React.ReactNode
  avatar: React.ReactNode
  photoUserTagContent?: React.ReactNode
  serialNumber: number
  onClick?: (content: any) => void
}

const getReplyingSectionContent = (authors: undefined | Array<string>, theme: 'light' | 'dark') => {
  if (!authors) return null
  if (authors.length <= 3) {
    return (
      <>
        Replying to{' '}
        {authors &&
          authors.map((author) => (
            <StyledUserLink
              mode={theme}
              href={`${TWITTER_URL}${author}`}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => {
                event.stopPropagation()
              }}
              key={author}
              displayTrailingSpace>
              {author}
            </StyledUserLink>
          ))}
      </>
    )
  }
  return (
    <>
      Replying to{' '}
      <StyledUserLink
        mode={theme}
        href={`${TWITTER_URL}${authors[0]}`}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => {
          event.stopPropagation()
        }}
        displayTrailingSpace>
        {authors[0]}
      </StyledUserLink>
      <StyledUserLink
        mode={theme}
        href={`${TWITTER_URL}${authors[1]}`}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => {
          event.stopPropagation()
        }}
        displayTrailingSpace>
        {authors[1]}
      </StyledUserLink>
      and{' '}
      <Tooltip mode={theme} enabled content={authors.slice(2).join(' ')}>
        <StyledOtherAuthorsSpan mode={theme}>{authors.length - 2} other(s)</StyledOtherAuthorsSpan>
      </Tooltip>
    </>
  )
}

const SoundbiteDefaultFrame = ({
  replySection = {
    display: false,
    author: undefined
  },
  retweetSection = {
    display: false,
    author: undefined,
    url: undefined,
    isVerified: false
  },
  reblogSection = {
    display: false,
    author: undefined,
    url: undefined
  },
  headerInfo,
  dateItem,
  domainIcon,
  postContent,
  actionItems,
  metadataItems,
  mediaItems,
  avatar,
  photoUserTagContent,
  hasWarningInfo = false,
  serialNumber,
  onClick
}: SoundbiteDefaultFrameProps) => {
  const theme = useDisplayTheme() as Theme
  const iconColor = sys[theme].text.placeholder
  const { t } = useTranslation()

  return (
    <StyledContainer
      mode={theme}
      data-testid={`${AutomationId.soundbiteDefaultFrame.post}-${serialNumber}`}>
      {avatar}
      <StyledContentContainer onClick={onClick}>
        <StyledHeaderContainer>
          <StyledHeaderLeft>{headerInfo}</StyledHeaderLeft>
          <StyledHeaderRight mode={theme}>
            {dateItem}
            <StyledIconContainer>{domainIcon}</StyledIconContainer>
          </StyledHeaderRight>
        </StyledHeaderContainer>
        {replySection.display && (
          <StyledInfoContainer mode={theme}>
            {getReplyingSectionContent(replySection.author, theme)}
          </StyledInfoContainer>
        )}
        <StyledContent mode={theme}>{postContent}</StyledContent>
        {retweetSection.display && (
          <StyledInfoContainer mode={theme}>
            <Retweet fontSize={14} color={iconColor} /> {t('retweetedBy')}{' '}
            <StyledUserLink
              mode={theme}
              href={retweetSection.url}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => {
                event.stopPropagation()
              }}
              displayTrailingSpace={!retweetSection.isVerified}>
              {retweetSection.author}
            </StyledUserLink>
            {retweetSection.isVerified && <TwitterBlueVerified mode={theme} fontSize={14} />}
          </StyledInfoContainer>
        )}
        {reblogSection.display && (
          <StyledInfoContainer mode={theme}>
            <Reblog fontSize={14} color={iconColor} /> Reblogged by{' '}
            <StyledUserLink
              mode={theme}
              href={reblogSection.url}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => {
                event.stopPropagation()
              }}
              displayTrailingSpace>
              {reblogSection.author}
            </StyledUserLink>
          </StyledInfoContainer>
        )}
        {photoUserTagContent}
        <StyledActionContainer>{actionItems}</StyledActionContainer>
        {hasWarningInfo && <WarningInfo />}
        {mediaItems}
        <StyledMetadataContainer mode={theme}>{metadataItems}</StyledMetadataContainer>
      </StyledContentContainer>
    </StyledContainer>
  )
}

export default SoundbiteDefaultFrame
